import { AppState } from "@store";
import { InvalidFormInfo, RdReduxForm, ValidFormInfo, FormBindings } from "rd-redux-forms";
import { AssetAttribute } from "../../api";
import { AssetAttributeFormMeta, getCurrentAssetDefForm } from "../adUnitAssetBuilderForm";
import { adUnitAssetSelector } from "./adUnitAssetSelector";
import { Dispatch } from "react-redux";
import { range } from "@common";

export function assetFormSelector(appState: AppState): AssetFormSelectionResult | undefined {
    const assets = adUnitAssetSelector(appState);
    const forms = getCurrentAssetDefForm();
    const data = appState.adUnitBuilder.assetForms;

    if (!assets || !forms) {
        return undefined;
    }

    const mainMeta: AssetAttributeFormMeta = {
        assetPropertyName: "meta",
        index: 0,
        formId: forms.main.form.id
    };

    return {
        main: {
            name: "main",
            form: forms.main.form,
            createBindings: (dispatch: Dispatch) => forms.main.bindings.bind(dispatch, mainMeta),
            assets: assets.main,
            data: forms.main.form.selector(data.main || forms.main.form.state.empty()),
            meta: mainMeta
        },
        nested: assets.nested.map<NestedAssetFormSelectionResult>(a => ({
            name: a.name,
            title: a.title,
            assets: a.attributes,
            form: forms.nested[a.name].form,
            isCollection: a.isCollection,
            min: a.min,
            max: a.max,
            data: range(a.isCollection ? Math.max(a.min, (data.nested[a.name] || []).length) : 1).map((_, index) => {
                const itemData = (data.nested[a.name] || [])[index];
                const form = forms.nested[a.name];
                const meta: AssetAttributeFormMeta = {
                    assetPropertyName: a.name,
                    index: index,
                    formId: form.form.id
                };
                return {
                    data: form.form.selector(itemData || form.form.state.empty()),
                    createBindings: (dispatch: Dispatch) => form.bindings.bind(dispatch, meta),
                    meta
                };
            })
        }))
    };
}

export function assetFormByKey(
    appState: AppState,
    assetPropertyName: string = "",
    index: number = 0
): AssetFormWithData | undefined {
    assetPropertyName = assetPropertyName || "main";

    if (!appState) {
        return undefined;
    }

    const forms = assetFormSelector(appState);

    if (!forms) {
        return undefined;
    }

    if (assetPropertyName === "main") {
        return forms.main;
    }

    const assetPropertyInfo = forms.nested.find(a => a.name === assetPropertyName);

    if (!assetPropertyInfo) {
        return undefined;
    }

    const data = assetPropertyInfo.data[index];
    if (!data) {
        return undefined;
    }

    return {
        name: assetPropertyName,
        assets: assetPropertyInfo.assets,
        createBindings: data.createBindings,
        data: data.data,
        form: assetPropertyInfo.form,
        meta: data.meta
    };
}

export interface AssetFormWithData {
    name: string;
    form: RdReduxForm<any, AssetAttributeFormMeta>;
    data: InvalidFormInfo<any> | ValidFormInfo<any>;
    meta: AssetAttributeFormMeta;
    createBindings: (dispatch: Dispatch) => FormBindings<any>;
    assets: AssetAttribute[];
}

export type AssetFormSelectionResult = {
    main: AssetFormWithData;
    nested: NestedAssetFormSelectionResult[];
};

export interface NestedAssetFormSelectionResult {
    name: string;
    title: string;
    form: RdReduxForm<any, AssetAttributeFormMeta>;
    assets: AssetAttribute[];
    isCollection: boolean;
    min: number;
    max: number;
    data: Array<{
        data: ValidFormInfo<any> | InvalidFormInfo<any>;
        meta: AssetAttributeFormMeta;
        createBindings: (dispatch: Dispatch) => FormBindings<any>;
    }>;
}
